import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { FolderKanban, Loader2, Undo2, ShieldCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { errorDetailText } from './ProjectJobSelect';

const API = `${window.__BACKEND_URL__}/api`;

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl p-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
    <p className="text-[11px] uppercase tracking-wide" style={{ color: '#64748B' }}>{label}</p>
    <p className="text-xl font-bold text-white" data-testid={testId}>{value}</p>
  </div>
);

// Admin-only: attribute every project-less time entry to "General Work" (attribution only, reversible).
export const GeneralWorkMigrationCard = ({ token }) => {
  const { toast } = useToast();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/time-entries/general-work/status`, auth); setStatus(data); } catch { /* card hides counts */ }
  }, [token]); // eslint-disable-line

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const preview = async () => {
    setBusy(true); setResult(null);
    try { const { data } = await axios.post(`${API}/time-entries/general-work/migrate`, { confirm: false }, auth); setPlan(data); }
    catch (e) { toast({ title: 'Preview failed', description: errorDetailText(e), variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!window.confirm(`Assign ${plan.entries} time entries (${plan.hours} h) to a project now? Only attribution fields change — times, pay and approvals are untouched. This can be rolled back.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time-entries/general-work/migrate`, { confirm: true }, auth);
      setResult(data); setPlan(null); loadStatus();
      toast({ title: 'Migration applied', description: `${data.entries_written} entries now carry a project.` });
    } catch (e) { toast({ title: 'Migration failed', description: errorDetailText(e), variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const rollback = async () => {
    if (!window.confirm('Remove the project from every entry that THIS migration tagged? Manual re-allocations are kept.')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/time-entries/general-work/rollback`, {}, auth);
      setResult(null); setPlan(null); loadStatus();
      toast({ title: 'Rolled back', description: `${data.entries_reverted} entries reverted.` });
    } catch (e) { toast({ title: 'Rollback failed', description: errorDetailText(e), variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mb-6 border-[#2D3748]" style={{ background: '#1A2433' }} data-testid="general-work-migration-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white text-base">
          <FolderKanban className="w-5 h-5" style={{ color: '#10B981' }} /> General Work — historical attribution
        </CardTitle>
        <p className="text-xs" style={{ color: '#A0AEC0' }}>
          A project is now required on every punch. Historical entries without a project can be assigned to the catch-all
          <span className="text-white font-medium"> GENERAL — General Work</span> (or to their job's project when the job has one).
          Attribution only: clock times, pay and approval status never change. Reversible.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {status && (
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Entries still without a project" value={status.entries_without_project} testId="gw-remaining" />
            <Stat label="Entries tagged by migration" value={status.migrated_entries} testId="gw-migrated" />
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={preview} disabled={busy} className="bg-[#1E88E5] text-white" data-testid="gw-preview-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />} Preview (read-only)
          </Button>
          {status?.migrated_entries > 0 && (
            <Button onClick={rollback} disabled={busy} variant="outline" className="border-[#2D3748] text-amber-300" data-testid="gw-rollback-btn">
              <Undo2 className="w-4 h-4 mr-1.5" /> Roll back migration
            </Button>
          )}
        </div>
        {plan && (
          <div className="rounded-xl p-4 space-y-3" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="gw-plan">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Entries" value={plan.entries} testId="gw-plan-entries" />
              <Stat label="Hours" value={plan.hours} testId="gw-plan-hours" />
              <Stat label="→ General Work" value={plan.to_general_work} testId="gw-plan-general" />
              <Stat label="→ job's project" value={plan.to_job_project} testId="gw-plan-jobproj" />
            </div>
            <p className="text-xs" style={{ color: '#A0AEC0' }}>
              {plan.workers} workers · {plan.open} open shifts · {plan.payroll_final} locked/paid (attribution still applied, pay untouched).
              Fields written: {plan.touches.join(', ')}.
            </p>
            {plan.months?.length > 0 && (
              <div className="max-h-40 overflow-y-auto text-xs" data-testid="gw-plan-months">
                {plan.months.map((m) => (
                  <div key={m.month} className="flex justify-between py-0.5" style={{ color: '#CBD5E1' }}>
                    <span>{m.month}</span><span>{m.entries} entries · {m.hours} h</span>
                  </div>
                ))}
              </div>
            )}
            {plan.entries > 0 ? (
              <Button onClick={apply} disabled={busy} className="bg-[#00C896] hover:bg-emerald-600 text-white" data-testid="gw-apply-btn">
                {busy ? 'Applying…' : `Assign ${plan.entries} entries now`}
              </Button>
            ) : <p className="text-sm" style={{ color: '#00C896' }}>Nothing to migrate — every entry already has a project.</p>}
          </div>
        )}
        {result && (
          <p className="text-sm" style={{ color: '#00C896' }} data-testid="gw-result">
            Done — {result.entries_written} entries written ({result.to_general_work} General Work, {result.to_job_project} job project).
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default GeneralWorkMigrationCard;
